// Turns a kernel Decision into plain language for one viewer.
// Inputs: the decision's own trace (labels, values, evidence, as redacted for this viewer), the live policy
// params, and the claims this viewer may see. Nothing is recomputed; nothing is a code or a score.
//
// The kernel labels every clause (R1). A refusal's copy is the string `deny.<label>`; there is no positional
// mapping and no shape check. A label with no copy yet falls back to a generic sentence, never a guess.

import type { ActorId, Claim, Decision, TraceNode } from '@model/index.ts';
import { STRINGS, items as itemsOf, t } from './i18n.ts';
import type { Key, Lang } from './i18n.ts';
import { canSee, claim, num, param, stewards, str, vouchesFor } from './queries.ts';
import type { World } from './world.ts';
import { fmtDate } from './format.ts';

// Pre-order: a parent before its children, siblings left to right, `if` before `then` before `else`.
const RANK: Record<string, number> = { over: 0, if: 1, then: 2, else: 3 };
function seg(s: string): [string, number] {
  const m = /^(\w+)\[(\d+)\]$/.exec(s);
  return m ? [m[1]!, Number(m[2])] : [s, -1];
}
function byPath(a: string, b: string): number {
  const x = a.split('.');
  const y = b.split('.');
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    if (x[i] === y[i]) continue;
    const [xn, xi] = seg(x[i]!);
    const [yn, yi] = seg(y[i]!);
    if (xn === yn) return xi - yi;
    return (RANK[xn] ?? 9) - (RANK[yn] ?? 9) || xn.localeCompare(yn);
  }
  return x.length - y.length;
}

// Labelled nodes in rule order, excluding the per-item copies inside a quantifier.
export function labelled(trace: TraceNode[]): TraceNode[] {
  return trace.filter((n) => n.label && !/\.(each|some)\[\d+\]/.test(n.path)).sort((a, b) => byPath(a.path, b.path));
}

// Is `child` reached from `parent` through conditions only (not through a value such as a limit)?
const CONDITION = /^((all|any|each|some)\[\d+\]|not)$/;
const conditionChild = (parent: TraceNode, child: TraceNode): boolean =>
  child.path.startsWith(`${parent.path}.`) && child.path.slice(parent.path.length + 1).split('.').every((s) => CONDITION.test(s));

// For a rule that is `any` of labelled branches (decide), the branch closest to passing is the one meant.
function relevant(trace: TraceNode[]): TraceNode[] {
  const nodes = labelled(trace);
  const branches = nodes.filter((n) => /^rule\.any\[\d+\]$/.test(n.path));
  if (!branches.length) return nodes;
  const score = (b: TraceNode): number => {
    const inside = nodes.filter((n) => conditionChild(b, n));
    return inside.length ? inside.filter((n) => n.ok).length / inside.length : 0;
  };
  const best = branches.reduce((a, b) => (score(b) > score(a) ? b : a));
  return nodes.filter((n) => n.path === best.path || conditionChild(best, n));
}

// The specific reason: the first failing labelled clause with no failing labelled clause beneath it.
export function reason(d: Decision): TraceNode | undefined {
  const failing = relevant(d.trace).filter((n) => !n.ok);
  return failing.find((n) => !failing.some((m) => m !== n && conditionChild(n, m)));
}

const hiddenUnder = (trace: TraceNode[], n: TraceNode): number =>
  trace.filter((x) => x.path === n.path || x.path.startsWith(`${n.path}.`)).reduce((s, x) => s + (x.hidden ?? 0), 0);

export type Gate = 'new' | 'regular' | 'friction';

// What the offer gate compared (R2): the live-item count against the limit, and which limit applied.
// When redaction cut a value because hidden claims fed it, the limit falls back to the published policy param.
export function offerGate(w: World, d: Decision) {
  const by = (l: string): TraceNode | undefined => d.trace.find((n) => n.label === l);
  const friction = by('friction.open-reports');
  const count = by('offer.under-limit');
  const lifted = by('ramp.rated')?.ok || by('ramp.settled-trade')?.ok;
  const gate: Gate = friction?.ok ? 'friction' : lifted ? 'regular' : 'new';
  const published = param(w, gate === 'friction' ? 'frictionOfferLimit' : gate === 'regular' ? 'offerLimit' : 'newcomerOfferLimit');
  const live = count?.matched ?? [];
  return {
    gate,
    limit: typeof count?.n === 'number' ? count.n : published,
    used: typeof count?.value === 'number' ? count.value : live.length + (count?.hidden ?? 0),
    live,
    reporters: typeof friction?.value === 'number' ? friction.value : null,
    reportsHidden: friction?.hidden ?? 0,
    ok: count?.ok ?? false,
  };
}

export type Action = 'takeDown' | 'ramp' | 'join' | 'wait' | 'readDecision';

export interface Why {
  admitted: boolean;
  headline: string;
  lines: string[];
  items: Claim[]; // evidence the viewer can see and act on (their own live items)
  decision?: Claim; // a steward decision the denial rests on
  hidden: number; // evidence this viewer may not see, counted by the kernel's redaction
  actions: Action[];
  rampTo?: number;
  label?: string; // the clause that failed
  summary: string; // one English line for the record pane
}

const hasCopy = (k: string): k is Key => k in STRINGS;

export function explain(w: World, predicate: string, d: Decision, viewer: ActorId, l?: Lang): Why {
  const visible = (id: string): Claim | undefined => {
    const c = claim(w, id);
    return c && canSee(w, viewer, c) ? c : undefined;
  };
  const g = w.gender(viewer);
  const say = (k: Key, vars: Record<string, string | number> = {}): string => t(k, { g, ...vars }, l);
  const en = (k: Key, vars: Record<string, string | number> = {}): string => t(k, { g, ...vars }, 'en');
  const base: Why = { admitted: d.admitted, headline: '', lines: [], items: [], hidden: 0, actions: [], summary: '' };

  if (d.admitted) return { ...base, summary: 'allowed' };
  const flat = (k: Key): Why => ({ ...base, headline: say(k), summary: en(k) });
  if (d.reason === 'bad-signature' || d.reason === 'bad-embedded-signature') return flat('deny.signature');
  if (d.reason === 'wrong-scope') return flat('deny.vouch.scope-bound');
  if (d.reason === 'rule-error') return { ...flat('deny.ruleError'), summary: `rule-error: ${d.error ?? ''}` };
  if (d.reason === 'bad-policy') return flat('deny.badPolicy');
  if (d.reason !== 'rule') return { ...flat('deny.unknown'), summary: `${d.reason}: ${en('deny.unknown')}` };

  const why = reason(d);
  const label = why?.label ?? '';

  if (label === 'offer.not-paused') {
    const active = (why ? d.trace.filter((n) => n.path.startsWith(`${why.path}.`)) : []).flatMap((n) => n.matched ?? []);
    const decision = active.map(visible).find(Boolean);
    const until = decision ? num(decision, 'until') : 0;
    return {
      ...base,
      headline: say('paused.title', { date: until ? fmtDate(until, l) : '' }),
      decision,
      hidden: why ? hiddenUnder(d.trace, why) : 0,
      actions: ['readDecision'],
      label,
      summary: en('paused.title', { date: until ? fmtDate(until, 'en') : '' }),
    };
  }

  if (label === 'offer.under-limit') {
    const gate = offerGate(w, d);
    const items = gate.live.map(visible).filter((c): c is Claim => !!c);
    const listed = items.length ? ` ${items.map((i) => str(i, 'title')).join('; ')}.` : '';
    const vars = (lang: Lang | undefined) => ({ items: itemsOf(gate.limit, lang), count: gate.used });
    if (gate.gate === 'friction') {
      const r = gate.reporters;
      const k: Key = r !== null ? 'deny.offer.friction' : 'deny.offer.frictionAtLeast';
      const v = (lang: Lang | undefined) => ({ ...vars(lang), reporters: r ?? param(w, 'frictionReporters') });
      return {
        ...base,
        headline: say(k, v(l)),
        lines: [say('deny.friction.fair'), say('deny.friction.hidden')],
        items,
        hidden: gate.reportsHidden,
        actions: ['takeDown', 'wait'],
        label,
        summary: en(k, v('en')) + listed,
      };
    }
    if (gate.gate === 'new') {
      return {
        ...base,
        headline: say('deny.offer.new', vars(l)),
        items,
        actions: ['takeDown', 'ramp'],
        rampTo: param(w, 'offerLimit'),
        label,
        summary: en('deny.offer.new', vars('en')) + listed,
      };
    }
    return { ...base, headline: say('deny.offer.regular', vars(l)), items, actions: ['takeDown'], label, summary: en('deny.offer.regular', vars('en')) + listed };
  }

  // Every other clause speaks for itself through its label.
  let key: Key = hasCopy(`deny.${label}`) ? (`deny.${label}` as Key) : 'deny.unknown';
  const voucher = vouchesFor(w, viewer)[0]?.author;
  if (key === 'deny.report.attested' && voucher) key = 'deny.report.attestedBy';
  const days = (ms: number): number => Math.round(ms / 86_400_000);
  const vars = (lang: Lang | undefined) => ({
    names: stewards(w)
      .map((s) => w.name(s, lang ?? w.language))
      .join(', '),
    voucher: voucher ? w.name(voucher, lang ?? w.language) : '',
    cap: param(w, predicate === 'vouch' ? 'vouchCap' : 'reportCap'),
    days: days(param(w, predicate === 'vouch' ? 'vouchWindow' : 'reportWindow')),
  });
  return {
    ...base,
    headline: say(key, vars(l)),
    hidden: why ? hiddenUnder(d.trace, why) : 0,
    actions: label.endsWith('.member') && label !== 'report.subject-member' ? ['join'] : [],
    label,
    summary: key === 'deny.unknown' ? `${label || d.reason}: ${en(key)}` : en(key, vars('en')),
  };
}

const OPS: Record<string, string> = { lt: '<', lte: '≤', gt: '>', gte: '≥', eq: '=' };
// The compared values of a node as one short expression (R2), e.g. "3 < 3" or "2 ≥ 2".
export function compared(n: TraceNode): string {
  if (n.value === undefined) return '';
  if (n.n === undefined) return `over ${JSON.stringify(n.value)}`;
  return `${JSON.stringify(n.value)} ${OPS[n.op ?? ''] ?? n.op ?? '?'} ${JSON.stringify(n.n)}`;
}
